// 롱숏 페어 스프레드 = OLS 잔차(ln P1 − β·ln P2 − c), ±1σ·±2σ 밴드.
// 국내주식_스프레드.xlsx 로직 재현: Stock1=롱 레그, Stock2=숏 레그.
//   Result_values Z = J − K·β − c  (J=lnP1, K=lnP2), 밴드 ±σ·±2σ, z = 최신/σ.

function toMap(points) {
  const m = new Map();
  for (const [ts, c] of points || []) if (c != null && c > 0) m.set(ts, c);
  return m;
}

// 두 시계열을 공통 거래일로 정렬
export function alignSeries(pts1, pts2) {
  const m1 = toMap(pts1), m2 = toMap(pts2);
  const ts = [...m1.keys()].filter(t => m2.has(t)).sort((a, b) => a - b);
  return { ts, p1: ts.map(t => m1.get(t)), p2: ts.map(t => m2.get(t)) };
}

// OLS y = c + β·x
function ols(x, y) {
  const n = x.length;
  let sx = 0, sy = 0;
  for (let i = 0; i < n; i++) { sx += x[i]; sy += y[i]; }
  const mx = sx / n, my = sy / n;
  let sxx = 0, sxy = 0;
  for (let i = 0; i < n; i++) { const dx = x[i] - mx; sxx += dx * dx; sxy += dx * (y[i] - my); }
  const beta = sxx > 0 ? sxy / sxx : 0;
  return { beta, intercept: my - beta * mx };
}

function std(arr) {
  const n = arr.length;
  if (n < 2) return 0;
  const m = arr.reduce((a, b) => a + b, 0) / n;
  return Math.sqrt(arr.reduce((a, b) => a + (b - m) * (b - m), 0) / (n - 1));
}

function corr(x, y) {
  const n = x.length;
  const mx = x.reduce((a, b) => a + b, 0) / n, my = y.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0, syy = 0;
  for (let i = 0; i < n; i++) { const dx = x[i] - mx, dy = y[i] - my; sxy += dx * dy; sxx += dx * dx; syy += dy * dy; }
  return (sxx > 0 && syy > 0) ? sxy / Math.sqrt(sxx * syy) : 0;
}

// pts1=Stock1(롱), pts2=Stock2(숏). null 반환 = 데이터 부족. minLen=최소 관측수(주봉이면 완화).
export function computeSpread(pts1, pts2, minLen = 30) {
  const { ts, p1, p2 } = alignSeries(pts1, pts2);
  if (ts.length < minLen) return null;
  const y = p1.map(Math.log), x = p2.map(Math.log);
  const { beta, intercept } = ols(x, y);
  const spread = y.map((yi, i) => yi - beta * x[i] - intercept);
  const sigma = std(spread);
  const z = sigma > 0 ? spread[spread.length - 1] / sigma : 0;

  // z 분해 — spread = [ln(P1/P2) − 평균] + [(1−β)·ln(P2) − 평균] 이므로 z도 두 항의 합.
  //   가격비 항: 두 종목 가격비(우선주면 할인율)가 자기 평균에서 벗어난 정도
  //   레벨 항 : β≠1 때문에 남는 숏 레그 가격수준 기여. β=1이면 0.
  // 두 항이 반대 부호로 클 때 합계 z만 보면 무슨 일인지 안 보여서 따로 노출한다.
  const mean = (a) => a.reduce((s, v) => s + v, 0) / a.length;
  const ratioArr = y.map((yi, i) => yi - x[i]);
  const levelArr = x.map((xi) => (1 - beta) * xi);
  const last = (a) => a[a.length - 1];
  const zRatio = sigma > 0 ? (last(ratioArr) - mean(ratioArr)) / sigma : 0;
  const zLevel = sigma > 0 ? (last(levelArr) - mean(levelArr)) / sigma : 0;
  const rows = ts.map((t, i) => ({
    t: t * 1000, p1: p1[i], p2: p2[i], ln1: y[i], ln2: x[i], spread: spread[i],
  }));
  let signal = "Neutral";
  if (z > 2) signal = "SHORT Spread";
  else if (z < -2) signal = "LONG Spread";
  return { rows, beta, intercept, sigma, z, zRatio, zLevel, logCorr: corr(x, y), signal, n: ts.length };
}
